import React, { useEffect, useMemo, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import videoApi from "@/lib/api";
import { sanitizeTextInputNoTrim } from "@/lib/security";

export const VideoReviewCard = ({ videoId, videoStatus, membership, isAdmin, language = "es" }) => {
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  const text = useMemo(() => (language === "es" ? {
    title: "¿Cómo fue tu transcripción de prueba?",
    description: "Déjanos una reseña rápida para mejorar Voxara.",
    rating: "Calificación",
    message: "Mensaje opcional",
    placeholder: "Cuéntanos cómo te fue con la transcripción, el resumen o el chat...",
    submit: "Enviar review",
    thanks: "Gracias por tu review",
  } : {
    title: "How was your trial transcription?",
    description: "Leave a quick review to help us improve Voxara.",
    rating: "Rating",
    message: "Optional message",
    placeholder: "Tell us how the transcription, summary, or chat felt...",
    submit: "Submit review",
    thanks: "Thanks for your review",
  }), [language]);

  const isEligible = !isAdmin && membership === "free" && videoStatus === "completed";

  useEffect(() => {
    if (!isEligible || !videoId) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const loadReview = async () => {
      try {
        const response = await videoApi.getVideoReview(videoId);
        if (!isMounted) return;
        const review = response.review || null;
        setExistingReview(review);
        setRating(review?.rating || 0);
        setMessage(review?.message || "");
      } catch (error) {
        console.error("Failed to load review", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadReview();
    return () => { isMounted = false; };
  }, [isEligible, videoId]);

  if (!isEligible || loading) return null;

  const handleSubmit = async () => {
    if (!rating) {
      toast.error(language === "es" ? "Selecciona una calificación" : "Select a rating");
      return;
    }

    setSubmitting(true);
    try {
      const response = await videoApi.submitVideoReview({
        video_id: videoId,
        rating,
        message: sanitizeTextInputNoTrim(message, 1500),
      });
      setExistingReview(response);
      setMessage(response.message || "");
      toast.success(language === "es" ? "Review enviada" : "Review submitted");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "es" ? "No pudimos guardar tu review" : "We could not save your review"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mx-auto mt-6 w-full max-w-[1120px] border-border bg-card p-5 2xl:max-w-[1240px]" data-testid="video-review-card">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold" data-testid="video-review-title">{existingReview ? text.thanks : text.title}</h3>
          <p className="text-sm text-muted-foreground">{text.description}</p>
        </div>

        <div className="space-y-2">
          <Label data-testid="video-review-rating-label">{text.rating}</Label>
          <div className="flex flex-wrap gap-2" data-testid="video-review-stars-row">
            {[1, 2, 3, 4, 5].map((value) => (
              <Button
                key={value}
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setRating(value)}
                className={rating >= value ? "border-amber-400 text-amber-400" : ""}
                data-testid={`video-review-star-${value}`}
              >
                <Star className={`h-4 w-4 ${rating >= value ? "fill-current" : ""}`} />
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="video-review-message">{text.message}</Label>
          <Textarea
            id="video-review-message"
            value={message}
            onChange={(event) => setMessage(sanitizeTextInputNoTrim(event.target.value, 1500))}
            placeholder={text.placeholder}
            className="min-h-[120px]"
            data-testid="video-review-message-input"
          />
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={handleSubmit} disabled={submitting} data-testid="video-review-submit-button">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {text.submit}
          </Button>
        </div>
      </div>
    </Card>
  );
};